import React from 'react';
import { connect } from 'react-redux';
import { formatNumber, getData, getDayjs } from '../../utils';
import loadable from '@loadable/component';
import Video from '../../components/ProductVideo';
import Price from '../../components/ProductPrice';
import Info from '../../components/ProductInfo';
import Review from '../../components/ProductReview';
import Helmet from 'react-helmet';
import Spinner from '../../components/Spinner';
import { fetchAllWishLists } from '../../reducer';

import './ProductPage.less';

const ProductGallery = loadable(
    () => import(/* webpackChunkName: "ProductGallery" */ '../../components/ProductGallery')
);

class ProductPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            content: null,
            loading: true,
            images: null,
            price: undefined,
            priceInt: undefined,
            specialPrice: undefined,
            description: null,
            sku: null,
            minWidth: 1150,
            width: props.width,
            resize: false,
            insurances: [],
            offerNumber: null,
            interactiveGallery: null,
            energyLabel: null,
            wishList: { productIds: [] },
            comparison: { productIds: [] },
            hideBundledProducts: false,
            wishLists: [],
            manufacturer: undefined,
            prodDetail: false,
        };
        this.getData = getData.bind(this);
        this.formatNumber = formatNumber.bind(this);

        this.alsoPurchasedSearch = {
            dataDesc: {
                limit: 10,
                offset: 0,
            },
            productId: this.props.prodId,
            languageId: 9,
            f: 'getAlsoPurchased',
            s: process.env.REACT_APP_STORE,
        };

        this.crossSellProductSearch = {
            limit: 15,
            productId: this.props.prodId,
            relationTypes: [2],
            languageId: 9,
        };

        this.bundledProductSearch = {
            limit: 20,
            productId: this.props.prodId,
            relationTypes: [5],
            languageId: 9,
        };

        this.bundlesProductBelongsToSearch = {
            productId: this.props.prodId,
            languageId: 9,
        };
    }

    componentDidMount() {
        this.getProdData();
        if (this.props.browserWidth <= this.state.minWidth && !this.state.resize) {
            this.setState({ resize: true });
        }
        this.props.store.dispatch(fetchAllWishLists());
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.browserWidth <= this.state.minWidth && !this.state.resize) {
            this.setState({ resize: true });
        }
        if (this.props.browserWidth > this.state.minWidth && this.state.resize) {
            this.setState({ resize: false });
        }
        if (this.props.cart.numberOfItems !== prevProps.cart.numberOfItems) {
            this.setCartQuantity(this.state.id);
        }
        if (this.props.wishList !== this.state.wishList) {
            this.setState({ wishList: this.props.wishList });
        }
        if (this.props.wishLists !== this.state.wishLists) {
            this.setState({ wishLists: this.props.wishLists });
        }
        if (this.props.comparison !== this.state.comparison) {
            this.setState({ comparison: this.props.comparison });
        }
    }

    setCartQuantity = (id) => {
        let quantityInCart = 0;
        let basketId;
        for (let i = 0; i < this.props.cart.products.length; i++) {
            let basket = this.props.cart.products[i];
            if (basket.prodId === id) {
                quantityInCart = basket.quantity;
                basketId = basket.basketId;
            }
        }
        this.setState({ quantityInCart: quantityInCart, basketId: basketId });
    };

    getManufacturer = (manuId) => {
        (async () => {
            try {
                let content = await this.getData('konakartjson', {
                    f: 'getManufacturer',
                    manufacturerId: manuId,
                    languageId: 9,
                });
                if (content.r) this.setState({ manufacturer: content.r });
            } catch (err) {
                console.log(err);
            }
        })();
    };

    getProdData = () => {
        (async () => {
            try {
                let content = await this.getData(this.props.url, this.props.body);
                if (content.r) {
                    let price = window.displayPriceWithTax
                        ? content.r.priceIncTax
                        : content.r.priceExTax;
                    let formattedPrice = this.formatNumber(price);
                    let priceInt = Math.round(price);

                    let specialPrice = window.displayPriceWithTax
                        ? content.r.specialPriceIncTax
                        : content.r.specialPriceExTax;
                    let formattedSpecialPrice = this.formatNumber(specialPrice);
                    let specialPriceInt = Math.round(specialPrice);
                    let syningareintak = content.r.syningareintokMiscItem?.itemValue;
                    let interactiveGallery = content.r.interactiveGallery?.itemValue;
                    let energyLabel = content.r.energyLabel;
                    let hideBundledProducts = !!content.r.hideBundledProducts;
                    this.setState(
                        {
                            content: content.r,
                            loading: false,
                            images: content.r.images.imgNames,
                            price: formattedPrice,
                            priceInt: priceInt,
                            specialPrice: formattedSpecialPrice,
                            specialPriceInt: specialPriceInt,
                            description: content.r.description,
                            sku: content.r.sku,
                            interactiveGallery: interactiveGallery,
                            offerNumber: content.r.offerNumber,
                            id: content.r.id,
                            syningareintak: syningareintak,
                            energyLabel: energyLabel,
                            hideBundledProducts: hideBundledProducts,
                        },
                        this.pushToDataLayer
                    );
                    this.setCartQuantity(content.r.id);
                    this.getManufacturer(content.r.manufacturerId);
                } else {
                    this.setState({ loading: false });
                }
            } catch (err) {
                console.log(err);
            }
        })();
    };

    getStructuredData = () => {
        let product = this.state.content;
        if (product != null) {
            let data = {
                '@context': 'http://schema.org',
                '@type': 'Product',
                brand: {
                    '@type': 'Brand',
                    name: product.manufacturerName,
                },
                name: product.name,
                image: window.imageBase + product.image,
                description: product.description.replace(/(<([^>]+)>)/gi, ' '),
                mpn: product.sku,
                sku: product.sku,
                productID: product.id,
                url: window.location.origin + '/SelectProd?prodId=' + product.id,
                offers: {
                    '@type': 'Offer',
                    priceCurrency: 'ISK',
                    price: this.state.priceInt,
                    itemCondition: 'http://schema.org/NewCondition',
                    url: window.location.origin + '/SelectProd?prodId=' + product.id,
                    availability:
                        product.quantity > 0 ||
                        product.akureyriQuantity > 0 ||
                        product.reykjavikQuantity > 0
                            ? 'http://schema.org/InStock'
                            : 'http://schema.org/OutOfStock',
                    seller: {
                        '@type': 'Organization',
                        name: process.env.REACT_APP_STORE_NAME,
                    },
                },
            };
            return (
                <Helmet
                    script={[
                        {
                            type: 'application/ld+json',
                            innerHTML: JSON.stringify(data),
                        },
                    ]}
                />
            );
        } else return null;
    };

    pushToDataLayer = () => {
        let product = this.state.content;
        if (product != null) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                ecommerce: {
                    detail: {
                        actionField: { list: '' },
                        products: [
                            {
                                name: product.name,
                                id: product.id,
                                price: this.state.specialPriceInt
                                    ? this.state.specialPriceInt
                                    : this.state.priceInt,
                                brand: product.manufacturerName,
                            },
                        ],
                    },
                },
            });
        }
    };

    shouldDisplayDateAvailable = (date) => {
        if (!date) return false;
        const now = getDayjs();
        const target = getDayjs(date);
        const diffMonths = target.diff(now, 'month');
        return !target.isBefore(now) && diffMonths < 3;
    };

    render() {
        const { content, loading, images, price, priceInt, specialPrice, specialPriceInt, sku } =
            this.state;
        if (loading) {
            return <Spinner className={'center-spinner'} size={120} />;
        } else {
            if (!content) return <div>Upplýsingar um þessa vöru finnast ekki</div>;
            const displayDateAvailable = this.shouldDisplayDateAvailable(content.dateAvailable);
            if (this.state.resize) {
                return (
                    <div>
                        {this.getStructuredData()}
                        <div className={'row' + (this.state.resize ? ' mobile' : '')}>
                            <h1 className={'prod-name mobile'}>{content.name}</h1>
                            <div className="prod-number">Vörunúmer: {sku}</div>
                            <Video
                                images={images}
                                videos={content.custom5}
                                interactiveGallery={this.state.interactiveGallery}
                                manufacturer={this.state.manufacturer}
                                product={content}
                                displayDateAvailable={displayDateAvailable}
                            />
                            <Price
                                product={content}
                                price={price}
                                priceInt={priceInt}
                                specialPrice={specialPrice}
                                specialPriceInt={specialPriceInt}
                                resize={this.state.resize}
                                syningareintak={this.state.syningareintak}
                                quantityInCart={this.state.quantityInCart}
                                basketId={this.state.basketId}
                                energyLabel={this.state.energyLabel}
                                productIdsOnWishList={this.state.wishList.productIds}
                                productIdsInComparison={this.state.comparison.productIds}
                                store={this.props.store}
                                wishLists={this.state.wishLists}
                                displayDateAvailable={displayDateAvailable}
                                isLoggedIn={this.props.isLoggedIn}
                                openTechnicalDescription={() => this.setState({ prodDetail: true })}
                            />
                            {(content.productType === 3 || content.productType === 4) &&
                            process.env.REACT_APP_STORE === 'store1' &&
                            !this.state.hideBundledProducts ? (
                                <div id={'bundled-products'}>
                                    <ProductGallery
                                        url={'/FetchRelatedProducts'}
                                        body={this.bundledProductSearch}
                                        heading={'Vörur í pakkanum'}
                                        bundle
                                        store={this.props.store}
                                    />
                                </div>
                            ) : (
                                ''
                            )}
                            <Review prodId={this.props.prodId} />
                            <Info
                                product={content}
                                mobile={true}
                                prodDetail={this.state.prodDetail}
                                openProdDetail={() => this.setState({ prodDetail: true })}
                                closeProdDetail={() => this.setState({ prodDetail: false })}
                            />
                        </div>
                        {(content.productType === 3 || content.productType === 4) &&
                        process.env.REACT_APP_STORE === 'tolvutek' &&
                        !this.state.hideBundledProducts ? (
                            <div id={'bundled-products'}>
                                <ProductGallery
                                    url={'/FetchRelatedProducts'}
                                    body={this.bundledProductSearch}
                                    heading={'Vörur í pakkanum'}
                                    bundle
                                    store={this.props.store}
                                />
                            </div>
                        ) : (
                            ''
                        )}
                        <div id={'cross-sell-products'}>
                            <ProductGallery
                                url={'/FetchRelatedProducts'}
                                body={this.crossSellProductSearch}
                                heading={'Fylgihlutir og hliðstæðar vörur'}
                                store={this.props.store}
                            />
                        </div>
                        {process.env.REACT_APP_STORE === 'store1' && (
                            <div id={'bundles-for-product'}>
                                <ProductGallery
                                    url={'/FetchBundlesForProduct'}
                                    body={this.bundlesProductBelongsToSearch}
                                    heading={'Pakkatilboð'}
                                    resize={false}
                                    store={this.props.store}
                                />
                            </div>
                        )}
                        <div id="also-purchased">
                            <ProductGallery
                                url={'/FetchAlsoPurchased'}
                                body={this.alsoPurchasedSearch}
                                heading={'Fólk sem keypti þetta keypti líka'}
                                store={this.props.store}
                            />
                        </div>
                        <div id="recently-viewed">
                            <ProductGallery
                                url={'/RecentlyViewed'}
                                body={{}}
                                heading={'Nýlega skoðað'}
                                store={this.props.store}
                            />
                        </div>
                    </div>
                );
            } else {
                return (
                    <div>
                        {this.getStructuredData()}
                        <div className="row">
                            <div style={{ width: '100%' }} className={'column-left'}>
                                <Video
                                    images={images}
                                    videos={content.custom5}
                                    interactiveGallery={this.state.interactiveGallery}
                                    manufacturer={this.state.manufacturer}
                                    product={content}
                                    displayDateAvailable={displayDateAvailable}
                                />
                                <Info
                                    product={content}
                                    prodDetail={this.state.prodDetail}
                                    openProdDetail={() => this.setState({ prodDetail: true })}
                                    closeProdDetail={() => this.setState({ prodDetail: false })}
                                />
                            </div>
                            <div style={{ width: '100%' }} className={'column-right'}>
                                <Price
                                    name={content.name}
                                    product={content}
                                    sku={sku}
                                    price={price}
                                    priceInt={priceInt}
                                    specialPrice={specialPrice}
                                    specialPriceInt={specialPriceInt}
                                    quantityInCart={this.state.quantityInCart}
                                    basketId={this.state.basketId}
                                    syningareintak={this.state.syningareintak}
                                    energyLabel={this.state.energyLabel}
                                    productIdsOnWishList={this.state.wishList.productIds}
                                    productIdsInComparison={this.state.comparison.productIds}
                                    store={this.props.store}
                                    wishLists={this.state.wishLists}
                                    kkSessionId={this.props.kkSessionId}
                                    displayDateAvailable={displayDateAvailable}
                                    isLoggedIn={this.props.isLoggedIn}
                                    openTechnicalDescription={() =>
                                        this.setState({ prodDetail: true })
                                    }
                                />
                                <Review prodId={this.props.prodId} />
                                {content.productType === 3 || content.productType === 4 ? (
                                    process.env.REACT_APP_STORE === 'store1' ? (
                                        !this.state.hideBundledProducts && (
                                            <div id={'bundled-products'}>
                                                <ProductGallery
                                                    url={'/FetchRelatedProducts'}
                                                    body={this.bundledProductSearch}
                                                    heading={'Vörur í pakkanum'}
                                                    resize={false}
                                                    slim={true}
                                                    bundle
                                                    store={this.props.store}
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div id={'cross-sell-products'}>
                                            <ProductGallery
                                                url={'/FetchRelatedProducts'}
                                                body={this.crossSellProductSearch}
                                                heading={'Fylgihlutir og hliðstæðar vörur'}
                                                resize={false}
                                                slim={true}
                                                store={this.props.store}
                                            />
                                        </div>
                                    )
                                ) : (
                                    <div id={'cross-sell-products'}>
                                        <ProductGallery
                                            url={'/FetchRelatedProducts'}
                                            body={this.crossSellProductSearch}
                                            heading={'Fylgihlutir og hliðstæðar vörur'}
                                            resize={false}
                                            slim={true}
                                            store={this.props.store}
                                        />
                                    </div>
                                )}
                                {process.env.REACT_APP_STORE === 'store1' && (
                                    <div id={'bundles-for-product'}>
                                        <ProductGallery
                                            url={'/FetchBundlesForProduct'}
                                            body={this.bundlesProductBelongsToSearch}
                                            heading={'Pakkatilboð'}
                                            resize={false}
                                            slim={true}
                                            store={this.props.store}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {content.productType === 3 || content.productType === 4 ? (
                            process.env.REACT_APP_STORE === 'tolvutek' ? (
                                !this.state.hideBundledProducts && (
                                    <div id={'bundled-products'}>
                                        <ProductGallery
                                            url={'/FetchRelatedProducts'}
                                            body={this.bundledProductSearch}
                                            heading={'Vörur í pakkanum'}
                                            bundle
                                            store={this.props.store}
                                        />
                                    </div>
                                )
                            ) : (
                                <div id={'cross-sell-products'}>
                                    <ProductGallery
                                        url={'/FetchRelatedProducts'}
                                        body={this.crossSellProductSearch}
                                        heading={'Fylgihlutir og hliðstæðar vörur'}
                                        resize={false}
                                        store={this.props.store}
                                    />
                                </div>
                            )
                        ) : (
                            ''
                        )}
                        <div id="also-purchased">
                            <ProductGallery
                                url={'/FetchAlsoPurchased'}
                                body={this.alsoPurchasedSearch}
                                heading={'Fólk sem keypti þetta keypti líka'}
                                store={this.props.store}
                            />
                        </div>
                        <div id="recently-viewed">
                            <ProductGallery
                                url={'/RecentlyViewed'}
                                body={{}}
                                heading={'Nýlega skoðað'}
                                store={this.props.store}
                            />
                        </div>
                    </div>
                );
            }
        }
    }
}

const mapStateToProps = (state) => {
    return state;
};

export default connect(mapStateToProps, null)(ProductPage);
